import type { ScreenerOHLC } from '@/types'
import type { ScalePoint } from './chartScale'
import { formatCompact } from './chartUtils'
import { isDisplayableOI, type OIDisplayPoint } from './oiDisplay'
import type { CandleGeometry } from './candleHitTest'

/** A carried display value must never inherit the observed candle's range. */
export function oiCandleValues(point: OIDisplayPoint): ScreenerOHLC | null {
  if (!isDisplayableOI(point) || point.filledFromTimestamp != null) return null
  const values = point.ohlc
  if (!values || ![values.open, values.high, values.low, values.close]
    .every((value) => typeof value === 'number' && Number.isFinite(value) && value >= 0)
    || values.low > Math.min(values.open, values.close)
    || values.high < Math.max(values.open, values.close)) return null
  return values
}

export function buildOICandlesticks(points: readonly OIDisplayPoint[], lineSeries?: readonly ScalePoint[]) {
  const series: CandleGeometry[] = points.map((point) => {
    const timestamp = point.timestamp * 1000
    if (!isDisplayableOI(point)) return [timestamp, null, null, null, null]
    const values = oiCandleValues(point)
    // A retained single value or display fill has no observed candle range.
    return values
      ? [timestamp, values.open, values.close, values.low, values.high]
      : [timestamp, null, null, null, null]
  })
  const candleTimes = new Set(series.filter((candle) => candle[1] != null).map((candle) => candle[0]))
  // Preserve the line's explicit breaks between distant coarse observations.
  const fallbackSeries: [number, number | null][] = (lineSeries ?? points.map((point) => [
    point.timestamp * 1000, isDisplayableOI(point) ? point.value : null,
  ] as ScalePoint)).map(([time, value]) => [time, candleTimes.has(time) ? null : value])
  // Fit complete wicks, separating candles so the shared line-scale helper
  // does not interpolate unobserved values across an empty time window.
  const scaleSeries: ScalePoint[][] = [3, 4].map((dimension) => series.flatMap((candle) => [
    [candle[0], candle[dimension]] as ScalePoint,
    [candle[0], null] as ScalePoint,
  ]))
  return { series, scaleSeries, fallbackSeries, hasCandles: series.some((candle) => candle[1] != null) }
}

export function oiCandleTooltipRow(point: OIDisplayPoint): string {
  const values = oiCandleValues(point)
  if (!values) return point.filledFromTimestamp != null ? ''
    : '<div style="color:#94a3b8;font-size:10px">Single-value sample · OHLC unavailable</div>'
  return `<div style="color:#94a3b8;font-size:10px">O ${formatCompact(values.open)} · H ${formatCompact(values.high)} · L ${formatCompact(values.low)} · C ${formatCompact(values.close)}</div>`
}
